import { Suspense, useEffect, useRef, useState } from "react";

function SectionSkeleton({ minHeight, label }) {
  const reservedHeight = typeof minHeight === "number" ? `${minHeight}px` : minHeight;

  return (
    <div
      className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 py-8"
      style={{ minHeight: reservedHeight }}
      aria-hidden="true"
    >
      <div className="w-full rounded-[28px] border border-slate-200 bg-white/75 p-5 shadow-sm backdrop-blur-sm sm:p-8">
        <div className="h-3 w-32 rounded-full bg-slate-200/80 animate-pulse" />
        <div className="mt-4 h-7 w-3/5 rounded-full bg-slate-200/80 animate-pulse" />
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="h-28 rounded-2xl bg-slate-100/80 animate-pulse" />
          <div className="h-28 rounded-2xl bg-slate-100/80 animate-pulse" />
        </div>
        <p className="mt-4 text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function DeferredSection({
  children,
  minHeight = 640,
  label = "Loading section",
  anchorId,
  rootMargin = "400px 0px",
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const reservedHeight = typeof minHeight === "number" ? `${minHeight}px` : minHeight;

  useEffect(() => {
    if (isVisible) return undefined;

    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return (
    <div
      ref={ref}
      className="relative"
      style={isVisible ? undefined : { minHeight: reservedHeight }}
    >
      {!isVisible && anchorId ? (
        <span id={anchorId} className="absolute left-0 top-0 h-px w-px" aria-hidden="true" />
      ) : null}

      {isVisible ? (
        <Suspense fallback={<SectionSkeleton minHeight={minHeight} label={label} />}>
          {children}
        </Suspense>
      ) : (
        <SectionSkeleton minHeight={minHeight} label={label} />
      )}
    </div>
  );
}
